import type { FluxErrorEntityErrorCodes } from '../types.js';

export const CardAssigneeErrorCodes = {
  CardAssigneeAlreadyExists: {
    code: 'card-assignee/already-exists',
    message: 'O usuário já está atribuído a este card.',
    explanation: 'O usuário que você está tentando atribuir a este card já está atribuído a ele.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Gerenciamento de Responsáveis',
        path: '/wiki/docs/cards/tabs/general#responsáveis',
      },
    ],
  },
  CardAssigneeNotAssigned: {
    code: 'card-assignee/not-assigned',
    message: 'O usuário não está atribuído a este card.',
    explanation: 'O usuário que você está tentando remover deste card não está atribuído a ele.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Gerenciamento de Responsáveis',
        path: '/wiki/docs/cards/tabs/general#responsáveis',
      },
    ],
  },
  CardAssigneeNotFound: {
    code: 'card-assignee/not-found',
    message: 'O usuário atribuído não foi encontrado.',
    explanation: 'O usuário atribuído que você está tentando acessar não foi encontrado.',
    localizations: {},
  },
  CardAssigneeUserNotMember: {
    code: 'card-assignee/user-not-member',
    message: 'O usuário atribuído não faz parte deste fluxo. Adicione-o ao fluxo antes de continuar.',
    explanation: 'O usuário que você está tentando atribuir a este card não é membro do fluxo (pipe). Por favor, adicione-o ao fluxo antes de continuar.',
    localizations: {},
  },
} as const satisfies FluxErrorEntityErrorCodes<'card-assignee'>;

export const CardAttachmentErrorCodes = {
  CardAttachmentNotFound: {
    code: 'card-attachment/not-found',
    message: 'Não foi possível encontrar o anexo.',
    explanation: 'O anexo de card que você está tentando acessar não foi encontrado.',
    localizations: {},
  },
} as const satisfies FluxErrorEntityErrorCodes<'card-attachment'>;

export const CardCommentAttachmentErrorCodes = {
  CardCommentAttachmentAlreadyExists: {
    code: 'card-comment-attachment/already-exists',
    message: 'Este anexo já está associado a este comentário.',
    explanation: 'O anexo que você está tentando adicionar a este comentário já está associado a ele.',
    localizations: {},
  },
  CardCommentAttachmentLimitReached: {
    code: 'card-comment-attachment/limit-reached',
    message: 'O número máximo de anexos permitidos para este comentário foi atingido.',
    explanation: 'Este comentário já atingiu o número máximo de anexos permitidos. Para adicionar mais anexos, remova alguns dos anexos existentes.',
    localizations: {},
  },
  CardCommentAttachmentNotFound: {
    code: 'card-comment-attachment/not-found',
    message: 'Não foi possível encontrar o anexo do comentário.',
    explanation: 'O anexo de comentário de card que você está tentando acessar não foi encontrado.',
    localizations: {},
  },
} as const satisfies FluxErrorEntityErrorCodes<'card-comment-attachment'>;

export const CardCommentErrorCodes = {
  CardCommentCreateFailed: {
    code: 'card-comment/create-failed',
    message: 'Não foi possível adicionar o comentário ao card.',
    explanation: 'Ocorreu um erro desconhecido ao tentar criar um comentário no card. Tente novamente mais tarde.',
    localizations: {},
  },
  CardCommentNotFound: {
    code: 'card-comment/not-found',
    message: 'O comentário especificado não foi encontrado.',
    explanation: 'O comentário de card que você está tentando acessar não foi encontrado.',
    localizations: {},
  },
  CardCommentNotOwner: {
    code: 'card-comment/not-owner',
    message: 'Você não tem permissão para editar ou excluir este comentário.',
    explanation: 'Você não é o proprietário deste comentário de card e, portanto, não pode editá-lo ou excluí-lo.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Aba de Comentários',
        path: '/wiki/docs/cards/tabs/comments',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'card-comment'>;

export const CardErrorCodes = {
  CardCreateFailed: {
    code: 'card/create-failed',
    message: 'Ocorreu um erro ao criar o processo. Verifique os dados fornecidos e tente novamente.',
    explanation: 'Ocorreu um erro ao tentar criar um novo card. Verifique se todos os campos obrigatórios estão preenchidos corretamente e tente novamente.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Criação de Processos',
        path: '/wiki/docs/cards/intro',
      },
      {
        name: 'Criação de Solicitações',
        path: '/wiki/docs/cards/solicitations',
      },
    ],
  },
  CardIsAlreadyArchived: {
    code: 'card/is-already-archived',
    message: 'Este processo já está arquivado.',
    explanation: 'O card que você está tentando arquivar já está arquivado.',
    localizations: {},
  },
  CardIsNotArchived: {
    code: 'card/is-not-archived',
    message: 'Este processo não está arquivado.',
    explanation: 'O card que você está tentando desarquivar não está arquivado.',
    localizations: {},
  },
  CardIsSyncLocked: {
    code: 'card/is-sync-locked',
    message: 'O processo está bloqueado durante uma automação síncrona.',
    explanation: 'O card está atualmente bloqueado por uma automação síncrona. Aguarde a conclusão da automação ou tente novamente mais tarde.',
    localizations: {},
  },
  CardLabelAlreadyLinked: {
    code: 'card/label-already-linked',
    message: 'Esta etiqueta já está associada ao processo.',
    explanation: 'A etiqueta que você está tentando associar a este card já está associada a ele.',
    localizations: {},
  },
  CardLabelNotLinked: {
    code: 'card/label-not-linked',
    message: 'A etiqueta especificada não está associada a este processo.',
    explanation: 'A etiqueta que você está tentando desassociar deste card não está associada a ele.',
    localizations: {},
  },
  CardLocked: {
    code: 'card/locked',
    message: 'O processo está bloqueado para edição. Isso pode ocorrer durante automações, manualmente ou por configurações de segurança.',
    explanation:
      'O card está atualmente bloqueado e não pode ser editado. Isso pode ocorrer devido a automações, bloqueios manuais ou configurações de segurança. Cards bloqueados não permitem alterações ou movimentações até que sejam desbloqueados.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Revisão do Processo',
        path: '/wiki/docs/cards/approvals/review-card',
      },
    ],
  },
  CardMoveSameStage: {
    code: 'card/move-same-stage',
    message: 'O processo já está nesta etapa do fluxo de trabalho. Selecione uma etapa diferente para movê-lo.',
    explanation: 'Você não pode mover um card para a etapa em que ele já se encontra. Isso pode acontecer em automações caso o processo já esteja na etapa de destino.',
    localizations: {},
  },
  CardNotFound: {
    code: 'card/not-found',
    message: 'O processo solicitado não foi encontrado. Ele pode ter sido arquivado, removido ou você pode não ter permissão para acessá-lo.',
    explanation: 'O card que você está tentando acessar não foi encontrado. Isso pode ocorrer se o card foi arquivado, removido ou se você não tem permissão para acessá-lo.',
    localizations: {},
  },
  CardNotSolicitation: {
    code: 'card/not-solicitation',
    message: 'Este processo não é uma solicitação.',
    explanation: 'Este card não é uma solicitação. Somente cards de solicitação podem executar esta ação.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Solicitações de Cards',
        path: '/wiki/docs/cards/solicitations',
      },
    ],
  },
  CardReadonly: {
    code: 'card/readonly',
    message: 'O processo está em modo somente leitura.',
    explanation: 'O card está em modo somente leitura e não pode ser editado. Isso pode ocorrer devido a restrições de permissão ou configurações do fluxo.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Revisão do Processo',
        path: '/wiki/docs/cards/approvals/review-card',
      },
      {
        name: 'Permissões do Card',
        path: '/wiki/docs/roles-and-permissions/permissions/permissions-fluxos#4-permissões-de-cards',
      },
    ],
  },
  CardReadonlyBySituation: {
    code: 'card/readonly-by-situation',
    message: 'O processo está em uma situação que não permite edição no momento.',
    explanation: 'O card não pode ser editado devido à sua situação atual (por exemplo, uma revisão pendente). Isso pode ser devido a uma revisão pendente ou outra restrição do fluxo de trabalho.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Revisão do Processo',
        path: '/wiki/docs/cards/approvals/review-card',
      },
    ],
  },
  CardStageNotFound: {
    code: 'card/stage-not-found',
    message: 'A etapa atual do card não foi encontrada.',
    explanation: 'A etapa atual do processo não foi encontrada no fluxo de trabalho.',
    localizations: {},
  },
  CardUpdateFailed: {
    code: 'card/update-failed',
    message: 'Não foi possível atualizar o processo.',
    explanation: 'Ocorreu um erro desconhecido ao tentar atualizar o card. Verifique os dados fornecidos e tente novamente.',
    localizations: {},
  },
} as const satisfies FluxErrorEntityErrorCodes<'card'>;

export const CardReviewErrorCodes = {
  CardReviewRequestedChanges: {
    code: 'card-review/requested-changes',
    message: 'Alterações foram solicitadas para esta revisão do card.',
    explanation: 'Foram solicitadas alterações para a revisão deste card e elas precisam ser atendidas antes de prosseguir.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Revisões de Cards',
        path: '/wiki/docs/cards/approvals/review-card',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'card-review'>;

export const CardSolicitationErrorCodes = {
  CardSolicitationForbidden: {
    code: 'card-solicitation/forbidden',
    message: 'Você não pode enviar essa solicitação no momento.',
    explanation: 'Você não tem permissão para enviar esta solicitação de card.',
    localizations: {},
  },
  CardSolicitationNotFormResponsible: {
    code: 'card-solicitation/not-form-responsible',
    message: 'Você não é responsável por este formulário e não pode enviá-lo.',
    explanation: 'Você não é o responsável por este formulário e, portanto, não pode enviá-lo.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Solicitações de Cards',
        path: '/wiki/docs/cards/solicitations',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'card-solicitation'>;

export const CardTaskAssigneeErrorCodes = {
  CardTaskAssigneeAlreadyExists: {
    code: 'card-task-assignee/already-exists',
    message: 'O usuário já está atribuído a esta tarefa.',
    explanation: 'O usuário que você está tentando atribuir a esta tarefa de card já está atribuído a ela.',
    localizations: {},
  },
  CardTaskAssigneeNotAssigned: {
    code: 'card-task-assignee/not-assigned',
    message: 'O usuário não está atribuído a esta tarefa.',
    explanation: 'O usuário que você está tentando remover desta tarefa de card não está atribuído a ela.',
    localizations: {},
  },
  CardTaskAssigneeNotFound: {
    code: 'card-task-assignee/not-found',
    message: 'O responsável da tarefa não foi encontrado.',
    explanation: 'O responsável da tarefa de card que você está tentando acessar não foi encontrado. Ele pode ter sido removido ou você pode não ter permissão para acessá-lo.',
    localizations: {},
  },
} as const satisfies FluxErrorEntityErrorCodes<'card-task-assignee'>;

export const CardTaskErrorCodes = {
  CardTaskBlockedByParent: {
    code: 'card-task/blocked-by-parent',
    message: 'Esta tarefa não pode ser finalizada porque sua tarefa pai ainda não foi concluída.',
    explanation: 'Esta tarefa de card não pode ser concluída porque sua tarefa pai ainda não foi concluída. Complete primeiro a tarefa da qual esta depende para poder prosseguir.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Tarefas Bloqueadas',
        path: '/wiki/docs/cards/tasks/blocked-tasks',
      },
    ],
  },
  CardTaskBlockedByStage: {
    code: 'card-task/blocked-by-stage',
    message: 'Esta tarefa foi criada em uma etapa à frente da etapa atual do processo e não pode ser finalizada ainda.',
    explanation:
      'Esta tarefa de card não pode ser concluída porque foi criada em uma etapa posterior à etapa atual do card. Mova o processo para a etapa correspondente ou aguarde a progressão natural do fluxo de trabalho para poder completar esta tarefa.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Tarefas Bloqueadas',
        path: '/wiki/docs/cards/tasks/blocked-tasks',
      },
    ],
  },
  CardTaskCardIdRequired: {
    code: 'card-task/card-id-required',
    message: 'O ID do card é obrigatório para realizar esta ação.',
    explanation: 'O ID do card é obrigatório para realizar esta ação na tarefa de card.',
    localizations: {},
  },
  CardTaskCardNotFound: {
    code: 'card-task/card-not-found',
    message: 'O processo associado a esta tarefa não foi encontrado.',
    explanation: 'O card associado a esta tarefa de card não foi encontrado. Verifique se o processo ainda existe, se não foi arquivado ou se você tem permissão para acessá-lo.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Criando uma Tarefa',
        path: '/wiki/docs/cards/tasks/creating-a-task',
      },
    ],
  },
  CardTaskCommentRequired: {
    code: 'card-task/comment-required',
    message: 'Um comentário é obrigatório para concluir esta tarefa.',
    explanation: 'A configuração desta tarefa exige que um comentário seja adicionado ao concluí-la.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Tarefas de Cards',
        path: '/wiki/docs/cards/tasks',
      },
    ],
  },
  CardTaskCreateFailed: {
    code: 'card-task/create-failed',
    message: 'Não foi possível criar a tarefa no processo.',
    explanation: 'Ocorreu um erro ao tentar criar uma nova tarefa no card. Verifique se você tem permissão para criar tarefas, se o template da tarefa existe e se todos os campos obrigatórios estão preenchidos corretamente.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Criando uma Tarefa',
        path: '/wiki/docs/cards/tasks/creating-a-task',
      },
    ],
  },
  CardTaskDeleteCreatedByStage: {
    code: 'card-task/delete-created-by-stage',
    message: 'Essa tarefa não pode ser excluída pois ela foi criada de um modelo da etapa.',
    explanation: 'Esta tarefa de card não pode ser excluída porque foi criada a partir de um modelo de etapa.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Tarefas de Etapas',
        path: '/wiki/docs/cards/tasks/stage-tasks',
      },
      {
        name: 'Modelos de Tarefas',
        path: '/wiki/docs/pipe/templates/tasks',
      },
    ],
  },
  CardTaskDueDateInThePast: {
    code: 'card-task/due-date-in-the-past',
    message: 'A data de vencimento da tarefa não pode ser no passado.',
    explanation: 'A data de vencimento da tarefa de card não pode ser uma data no passado.',
    localizations: {},
  },
  CardTaskInvalidDueDateFilter: {
    code: 'card-task/invalid-due-date-filter',
    message: 'O filtro de data de vencimento especificado é inválido.',
    explanation: 'O filtro de data de vencimento especificado para a tarefa de card é inválido.',
    localizations: {},
  },
  CardTaskMaxTasksReached: {
    code: 'card-task/max-tasks-reached',
    message: 'O número máximo de tarefas permitidas foi atingido para este processo.',
    explanation: 'Este card já atingiu o número máximo de tarefas permitidas. Remova algumas tarefas existentes para adicionar novas.',
    localizations: {},
  },
  CardTaskNotFound: {
    code: 'card-task/not-found',
    message: 'A tarefa especificada não foi encontrada.',
    explanation: 'A tarefa de card que você está tentando acessar não foi encontrada. Ela pode ter sido removida, você pode não ter permissão para acessá-la, ou o ID pode estar incorreto.',
    localizations: {},
  },
  CardTaskParentCardMismatch: {
    code: 'card-task/parent-card-mismatch',
    message: 'O card da tarefa pai não corresponde ao card da tarefa atual.',
    explanation: 'O card da tarefa pai não é o mesmo que o card da tarefa atual.',
    localizations: {},
  },
  CardTaskParentTaskSelf: {
    code: 'card-task/parent-task-self',
    message: 'Uma tarefa não pode depender de si mesma.',
    explanation: 'Uma tarefa de card não pode ser sua própria tarefa pai.',
    localizations: {},
  },
  CardTaskPipeIdRequired: {
    code: 'card-task/pipe-id-required',
    message: 'O ID do fluxo é obrigatório para criar uma tarefa.',
    explanation: 'O ID do fluxo (pipe) é obrigatório para criar uma tarefa de card.',
    localizations: {},
  },
  CardTaskRequiredFieldsNotAnswered: {
    code: 'card-task/required-fields-not-answered',
    message: 'O formulário não foi preenchido corretamente. Verifique os campos obrigatórios.',
    explanation: 'Um ou mais campos obrigatórios no formulário da tarefa de card não foram preenchidos.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Criando uma Tarefa',
        path: '/wiki/docs/cards/tasks/creating-a-task#formulário-personalizado',
      },
      {
        name: 'Formulários',
        path: '/wiki/docs/pipe/forms',
      },
    ],
  },
  CardTaskUpdateCreatedByStage: {
    code: 'card-task/update-created-by-stage',
    message: 'Essa tarefa não pode ser atualizada pois ela foi criada de um modelo da etapa.',
    explanation: 'Esta tarefa de card não pode ser atualizada porque foi criada a partir de um modelo de etapa.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Tarefas de Etapas',
        path: '/wiki/docs/cards/tasks/stage-tasks',
      },
      {
        name: 'Modelos de Tarefas',
        path: '/wiki/docs/pipe/templates/tasks',
      },
    ],
  },
  CardTaskUpdateFailed: {
    code: 'card-task/update-failed',
    message: 'Não foi possível atualizar a tarefa.',
    explanation: 'Ocorreu um erro ao tentar atualizar as informações da tarefa de card. Verifique se você tem permissão para editar, se todos os campos obrigatórios estão preenchidos corretamente e se a tarefa não está bloqueada.',
    localizations: {},
  },
  CardTaskUpdateWhileCompleted: {
    code: 'card-task/update-while-completed',
    message: 'Não é possível atualizar a tarefa porque ela já foi concluída.',
    explanation: 'Você não pode atualizar uma tarefa de card que já foi concluída.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Alterando o Status de uma Tarefa',
        path: '/wiki/docs/cards/tasks/updating-task-status',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'card-task'>;

export const CardTaskLabelErrorCodes = {
  CardTaskLabelAlreadyLinked: {
    code: 'card-task-label/already-linked',
    message: 'Esta etiqueta já está associada à tarefa.',
    explanation: 'A etiqueta que você está tentando associar a esta tarefa de card já está associada a ela.',
    localizations: {},
  },
  CardTaskLabelNotLinked: {
    code: 'card-task-label/not-linked',
    message: 'A etiqueta não está associada à tarefa.',
    explanation: 'A etiqueta que você está tentando desassociar desta tarefa de card não está associada a ela.',
    localizations: {},
  },
} as const satisfies FluxErrorEntityErrorCodes<'card-task-label'>;
